import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, ButtonGroup, Col, Row } from 'reactstrap';
import Moment from 'react-moment';

import XMTable from '../../components/XMTable';
import XMSearchBox from '../../components/XMSearchBox';
import XMPagination from '../../components/XMPagination';
import * as appStoreActions from '../../actions/appStoreActions';
import { AppCell, TypePill, NoResults, sourceLabel } from './rowParts';
import { PAGE_SIZE, searchedFor, sortedBy } from './listQuery';

const HEADERS = [
  { name: 'App', code: 'name', sortable: true },
  { name: 'Source', style: { width: '8rem' } },
  { name: 'Type', style: { width: '7rem' } },
  { name: 'Last Updated', code: 'last-updated', sortable: true },
  {
    name: 'Install Count',
    code: 'install-count',
    sortable: true,
    style: { width: '8rem' },
    hint: 'Every store that installed this app, through an advert or not. The adverts tab counts one advert only, so the two numbers can differ.'
  },
  { name: 'Advert', style: { width: '7rem' } },
  { name: 'Actions', style: { width: '9rem' } }
];

// The chips filter server-side (UN-3339); `count` names each chip's number in GET /apps/counts,
// which counts the subsets over the whole search, not the page on screen.
export const FILTERS = [
  { key: 'all', label: 'All', count: 'all' },
  { key: 'with-advert', label: 'With advert', count: 'withAdvert' },
  { key: 'no-advert', label: 'No advert', count: 'noAdvert' }
];

// The label map lives with typeLabel in rowParts; re-exported so the tests and any importer keep working.
export { sourceLabel };

/**
 * Server-paged, server-searched and server-filtered (UN-3339): the tab holds one page, and the chip
 * counts come from their own read over the search, so they stay right whichever page is showing.
 */
const AllApps = ({ appsPage, appsPageLoaded, appCounts, error, dispatch }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState({ search: '', filter: 'all', page: 0, sortBy: null, direction: null });
  const { search, filter } = query;

  // Caught for the same reason as the adverts tab: the reducer holds the message, and an
  // uncaught rejection is otherwise logged as unhandled on every failure.
  useEffect(() => {
    dispatch(appStoreActions.getAppsPage({ ...query, size: PAGE_SIZE })).catch(() => {});
  }, [dispatch, query]);

  useEffect(() => {
    dispatch(appStoreActions.getAppCounts(search)).catch(() => {});
  }, [dispatch, search]);

  const handleSort = (sort, sortDir) => setQuery(q => sortedBy(q, sort, sortDir));

  const active = FILTERS.find(f => f.key === filter);
  const rows = appsPage.content;

  return (
    <div>
      <p className="text-muted">The full app catalog, with or without a published advert.</p>
      <Row className="mb-3">
        <Col md="5">
          <XMSearchBox onSearch={({ fuzzyString }) => setQuery(q => searchedFor(q, fuzzyString))} />
        </Col>
        <Col className="text-end">
          <ButtonGroup>
            {FILTERS.map(f => (
              <Button
                key={f.key}
                color={filter === f.key ? 'dark' : 'light'}
                onClick={() => setQuery(q => (q.filter === f.key ? q : { ...q, page: 0, filter: f.key }))}
              >
                {f.label}
                {appCounts ? ` (${appCounts[f.count] ?? 0})` : ''}
              </Button>
            ))}
          </ButtonGroup>
        </Col>
      </Row>

      {error ? <Alert color="danger">{error}</Alert> : null}

      <XMTable headers={HEADERS} handleSort={handleSort}>
        {/* Three states, not one. Before this the same row said "No apps match." while the
            request was still in flight and again when it had failed, so a 404 read as an empty
            catalog from first paint. */}
        {!appsPageLoaded && !error ? (
          <NoResults colSpan={HEADERS.length}>Loading the app catalog&hellip;</NoResults>
        ) : null}
        {appsPageLoaded && !error && rows.length === 0 ? (
          <NoResults colSpan={HEADERS.length}>
            No apps match{search ? ` \u201c${search}\u201d` : ''}
            {filter === 'all' ? '' : ` under \u201c${active.label}\u201d`}.
          </NoResults>
        ) : null}
        {rows.map(app => (
          <tr key={app.uuid}>
            <td className="align-middle">
              <AppCell name={app.name} subtitle={app.sourceKey} imageUrl={app.imageUrl} />
            </td>
            <td className="align-middle">{sourceLabel(app.source)}</td>
            <td className="align-middle">
              <TypePill type={app.type} />
            </td>
            <td className="align-middle">
              <Moment format="YYYY-MM-DD HH:mm">{app.lastUpdated}</Moment>
            </td>
            <td className="align-middle">
              <Link to={`/manage-apps/apps/${app.uuid}/installed-sites`}>{app.installCount ?? 0}</Link>
            </td>
            <td className="align-middle">
              {app.advertUuid ? <Badge color="primary">ADVERT</Badge> : null}
            </td>
            <td className="align-middle">
              {app.advertUuid ? (
                <Button
                  size="sm"
                  color="light"
                  className="border"
                  onClick={() => navigate(`/manage-apps/adverts/${app.advertUuid}/edit`)}
                >
                  Edit advert
                </Button>
              ) : (
                <Button
                  size="sm"
                  color="dark"
                  onClick={() => navigate(`/manage-apps/adverts/new?appUuid=${app.uuid}`)}
                >
                  Create advert
                </Button>
              )}
            </td>
          </tr>
        ))}
      </XMTable>
      <XMPagination pagination={appsPage} paginate={page => setQuery(q => ({ ...q, page }))} />
    </div>
  );
};

export default connect(state => ({
  appsPage: state.appStore.appsPage,
  appsPageLoaded: state.appStore.appsPageLoaded,
  appCounts: state.appStore.appCounts,
  error: state.appStore.error
}))(AllApps);
